import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { LogOut, Mail, Phone, Calendar, Building2, MessageSquare, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const [inquiries, setInquiries] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminEmail, setAdminEmail] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchData();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminEmail(response.data.email);
    } catch (error) {
      console.error("Auth error:", error);
      localStorage.removeItem("adminToken");
      navigate("/admin/login");
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) return;

    try {
      const [inquiriesRes, consultationsRes] = await Promise.all([
        axios.get(`${API}/admin/inquiries`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/consultations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setInquiries(inquiriesRes.data);
      setConsultations(consultationsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (type, id, newStatus) => {
    const token = localStorage.getItem("adminToken");
    try {
      await axios.patch(
        `${API}/admin/${type}/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (type === "inquiries") {
        setInquiries(inquiries.map(item =>
          item.id === id ? { ...item, status: newStatus } : item
        ));
      } else {
        setConsultations(consultations.map(item =>
          item.id === id ? { ...item, status: newStatus } : item
        ));
      }

      toast.success("Status updated successfully");
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
    toast.success("Logged out successfully");
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
      case 'pending':
        return 'bg-accent-sand text-neutral-charcoal';
      case 'in-progress':
        return 'bg-primary text-white';
      case 'completed':
        return 'bg-accent-sage text-white';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F3] flex items-center justify-center">
        <p className="text-neutral-gray">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F3]">
      {/* Header */}
      <div className="bg-primary-dark text-white py-6 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold font-heading">Admin Dashboard</h1>
              <p className="text-sm text-gray-300 mt-1">Logged in as: {adminEmail}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-primary-dark"
              data-testid="logout-button"
            >
              <LogOut className="mr-2" size={16} />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-sm text-neutral-gray mb-2">Total Inquiries</p>
                <p className="text-3xl font-bold text-primary font-heading">{inquiries.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-sm text-neutral-gray mb-2">New Inquiries</p>
                <p className="text-3xl font-bold text-accent-sand font-heading">
                  {inquiries.filter(i => i.status === 'new').length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-sm text-neutral-gray mb-2">Total Consultations</p>
                <p className="text-3xl font-bold text-primary font-heading">{consultations.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <p className="text-sm text-neutral-gray mb-2">Pending Consultations</p>
                <p className="text-3xl font-bold text-accent-sand font-heading">
                  {consultations.filter(c => c.status === 'pending').length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="inquiries" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="inquiries" data-testid="inquiries-tab">Inquiries</TabsTrigger>
            <TabsTrigger value="consultations" data-testid="consultations-tab">Consultations</TabsTrigger>
          </TabsList>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <div className="space-y-4">
              {inquiries.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-neutral-gray">
                    No inquiries yet
                  </CardContent>
                </Card>
              ) : (
                inquiries.map((inquiry) => (
                  <Card key={inquiry.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-xl font-heading mb-2">{inquiry.name}</CardTitle>
                          <div className="flex flex-wrap gap-3 text-sm text-neutral-gray">
                            <div className="flex items-center gap-1">
                              <Mail size={14} />
                              <span>{inquiry.email}</span>
                            </div>
                            {inquiry.phone && (
                              <div className="flex items-center gap-1">
                                <Phone size={14} />
                                <span>{inquiry.phone}</span>
                              </div>
                            )}
                            {inquiry.organization && (
                              <div className="flex items-center gap-1">
                                <Building2 size={14} />
                                <span>{inquiry.organization}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Calendar size={14} />
                              <span>{formatDate(inquiry.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={getStatusColor(inquiry.status)}>{inquiry.status}</Badge>
                          <Select
                            value={inquiry.status}
                            onValueChange={(value) => handleStatusChange('inquiries', inquiry.id, value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="in-progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-start gap-2">
                        <MessageSquare size={16} className="text-neutral-gray mt-1 flex-shrink-0" />
                        <p className="text-neutral-charcoal whitespace-pre-wrap">{inquiry.message}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Consultations Tab */}
          <TabsContent value="consultations">
            <div className="space-y-4">
              {consultations.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-neutral-gray">
                    No consultation requests yet
                  </CardContent>
                </Card>
              ) : (
                consultations.map((consultation) => (
                  <Card key={consultation.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-xl font-heading mb-2">{consultation.name}</CardTitle>
                          <div className="flex flex-wrap gap-3 text-sm text-neutral-gray">
                            <div className="flex items-center gap-1">
                              <Mail size={14} />
                              <span>{consultation.email}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Phone size={14} />
                              <span>{consultation.phone}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Building2 size={14} />
                              <span>{consultation.organization}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar size={14} />
                              <span>{formatDate(consultation.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={getStatusColor(consultation.status)}>{consultation.status}</Badge>
                          <Select
                            value={consultation.status}
                            onValueChange={(value) => handleStatusChange('consultations', consultation.id, value)}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in-progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-semibold text-neutral-charcoal mb-1">Service Type:</p>
                          <p className="text-neutral-gray">{consultation.service_type}</p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-neutral-charcoal mb-1">Preferred Date:</p>
                          <p className="text-neutral-gray">{consultation.preferred_date}</p>
                        </div>
                        {consultation.additional_info && (
                          <div>
                            <p className="text-sm font-semibold text-neutral-charcoal mb-1">Additional Information:</p>
                            <p className="text-neutral-gray whitespace-pre-wrap">{consultation.additional_info}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
